const post = {
  slug: 'terraform-lessons-from-the-trenches',
  number: 17,
  title: 'Infrastructure as Code: Terraform Lessons From the Trenches',
  date: '2025-08-19',
  readTime: '7 min read',
  views: 47,
  category: 'devops',
  tags: ['Terraform', 'DevOps', 'IaC'],
  excerpt:
    'Terraform makes infrastructure reproducible, which is a genuine gift. It also has a few sharp edges that everyone eventually meets. Here are the ones worth knowing before they cut you.',
  cover: null,
  content: `
Before infrastructure as code, environments were built by hand and documented in someone's head. You could never be quite sure staging matched production, and rebuilding from scratch was a frightening prospect nobody wanted to test. Terraform fixes that by turning your infrastructure into files you can read, review, and reproduce. It is a genuine gift. It also has a few sharp edges, and I have caught most of them the hard way, so let me hand you the map before you walk into them yourself.

## The state file is the whole game

The single most important thing to understand about Terraform is the state file. It is how Terraform remembers what it has already created, the map between your code and the real resources in the cloud. Almost every confusing Terraform problem I have debugged traces back to state in the end.

Two rules save you most of the pain. Store the state remotely, in a shared backend, never on one laptop, so the whole team works from the same source of truth. And lock it during changes, so two people cannot run an apply at the same time and corrupt it.

\`\`\`text
Local state on a laptop  -> drift, conflicts, eventually a corrupted file
Remote state + locking   -> one shared truth, no concurrent-apply disasters
\`\`\`

Get those two right on day one, because learning them after a corrupted state file is one of the worse days you will have with this tool.

## Plan before you apply, every time

Terraform has a feature most tools wish they had: it will show you exactly what it intends to do before it does anything.

\`\`\`bash
terraform plan    # shows what will change
terraform apply   # actually makes the changes
\`\`\`

Read the plan. Actually read it, do not skim. The number of incidents that begin with someone glancing past a plan that quietly said it would destroy and recreate a database is not small, and I have been in the room for one. The plan is your safety check, and the lines that say "destroy" then "create" on something stateful are the ones that ruin afternoons. Treating plan output as a formality throws away the best safety feature the tool gives you.

## Make it modular, but not too soon

As your infrastructure grows, a single giant file becomes unmanageable. Modules let you package a piece of infrastructure once and reuse it, which keeps things consistent and saves repetition. A well-made module for a standard service is a real force multiplier across a team.

The trap is abstracting too early. A module built before you understand the real variation tends to be wrong, and a bad abstraction is harder to live with than a little duplication. Let the patterns reveal themselves across two or three real uses, then extract the module from what you have actually seen. Premature modules cost more than the copying they were meant to save.

## Do not mix by-hand changes with code

The fastest way to break a Terraform setup is to change something by hand in the cloud console and leave the code untouched. Now reality and the code disagree, and the next apply will either fail confusingly or quietly undo your manual fix. This drift is one of the most common sources of Terraform frustration, and it is entirely self-inflicted.

The discipline is simple to state and requires real commitment to keep: if it is managed by Terraform, you change it through Terraform, never through the console. The moment there are two ways to change infrastructure, your code stops being the source of truth and becomes a hopeful suggestion that the cloud may or may not agree with.

## A checklist for staying out of trouble

- State lives in a shared remote backend, with locking enabled.
- Every apply is preceded by a plan that a human actually reads.
- Modules are extracted from real, repeated patterns, not invented up front.
- Nothing managed by Terraform is ever edited by hand in the console.
- Plans and applies run through CI, so changes are reviewed like code.

## Why it is still worth it

For all its sharp edges, the payoff is large. Your infrastructure becomes reviewable in a pull request, reproducible in a new region, and recoverable after a disaster, because it is no longer trapped in someone's memory. Respect the state file, always read the plan, modularize only once the patterns are real, and never edit by hand. Do that, and Terraform turns infrastructure from a fragile thing built by hand into something you can engineer with the same confidence as the rest of your system.
`,
};

export default post;
